import React, { useEffect, useMemo, useState } from "react";
import { FaSave, FaTimes } from "react-icons/fa";
import Button from "../../../components/Button/Button";
import { getAllUsers } from "../../../services/userServices";
import { getArticles } from "../../../services/articlesServices";
import { createReglement } from "../../../services/achatsServices";
import { sendNotification } from "../../../services/notificationServices";
import { toast } from "react-toastify";
import Select from "react-select";

const STATUS = [
  "Non Réglé",
  "Réglé",
  "En Attente",
  "Annulé",
  "Remboursé",
  "Partiellement Réglé",
];

const PAYMENT_TYPES = ["En ligne", "Chèque", "Espèces", "Virement", "CB", "HelloAsso", "Chèques Vacances", "Coupons Sports", "Autre"];

export default function CreateAchatModal({ isActive, onClose, refreshAchats }) {
  const [users, setUsers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedArticle, setSelectedArticle] = useState("");
  const [quantite, setQuantite] = useState(1);
  const [dateAchat, setDateAchat] = useState("");
  const [messageAchat, setMessageAchat] = useState("");
  const [statutReglement, setStatutReglement] = useState("");
  const [typeReglement, setTypeReglement] = useState("");
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);

  // Options Select users (avec adhérents)
  const userOptions = useMemo(
    () =>
      users.map((u) => {
        const adherentsLabel = u.adherents?.length
          ? ` (${u.adherents.map((a) => `${a.prenom.trim()} ${a.nom}`).join(", ")})`
          : "";
        return { value: u.id, label: `${u.nom} ${u.prenom}${adherentsLabel}` };
      }),
    [users]
  );

  const selectedArticleObj = useMemo(
    () => articles.find((a) => String(a.id) === String(selectedArticle)),
    [articles, selectedArticle]
  );
  const total = useMemo(
    () => ((Number(selectedArticleObj?.prix) || 0) * (Number(quantite) || 0)).toFixed(2),
    [selectedArticleObj, quantite]
  );

  // Charger données à l'ouverture
  useEffect(() => {
    if (!isActive) return;
    (async () => {
      try {
        setLoading(true);
        const [usersData, articlesData] = await Promise.all([getAllUsers(), getArticles()]);
        setUsers(usersData || []);
        setArticles(articlesData || []);
        setToken(localStorage.getItem("token") || "");
        // date du jour par défaut
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, "0");
        const dd = String(today.getDate()).padStart(2, "0");
        setDateAchat(`${yyyy}-${mm}-${dd}`);
      } catch (e) {
        console.error(e);
        toast.error("Erreur lors du chargement des données.");
      } finally {
        setLoading(false);
      }
    })();
  }, [isActive]);

  // Réinitialiser proprement quand on ferme
  const resetForm = () => {
    setSelectedUser("");
    setSelectedArticle("");
    setQuantite(1);
    setDateAchat("");
    setMessageAchat("");
    setStatutReglement("");
    setTypeReglement("");
  };

  const handleSendNotification = async (recipientId) => {
    const message =
      "Le responsable du club vous a affecté un règlement d'un article de la boutique à régler sur la page Commandes & Règlements.";
    await sendNotification(message, recipientId, token);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUser) return toast.warn("Sélectionnez un utilisateur.");
    if (!selectedArticle) return toast.warn("Sélectionnez un article.");
    if (!statutReglement) return toast.warn("Sélectionnez un statut de règlement.");
    if (!typeReglement) return toast.warn("Sélectionnez un type de règlement.");

    const achatData = {
      user_achat_id: selectedUser,
      article_id: selectedArticle,
      quantite: Number(quantite) || 1,
      date_achat: dateAchat,
      message_achat: messageAchat,
      statut_reglement: statutReglement,
      type_reglement: typeReglement,
    };

    try {
      setLoading(true);
      await createReglement(achatData);
      await handleSendNotification(selectedUser);
      toast.success("Achat ajouté avec succès.");
      refreshAchats?.();
      resetForm();
      onClose();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la création de l'achat.");
    } finally {
      setLoading(false);
    }
  };

  if (!isActive) return null;

  return (
    <div className="fixed inset-0 z-[70]">
      {/* overlay */}
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      {/* modal */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-2xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 overflow-hidden">
          {/* header dégradé */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 flex items-center justify-between">
            <h3 className="text-white font-semibold">Ajouter un nouvel achat d'article Boutique</h3>
            <button
              type="button"
              onClick={() => {
                resetForm();
                onClose();
              }}
              className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-3 py-1.5 text-white hover:bg-white/20"
            >
              <FaTimes />
              <span>Fermer</span>
            </button>
          </div>

          {/* body */}
          <form
            onSubmit={handleSubmit}
            onKeyDown={(e) => {
              // Empêche la validation involontaire via Entrée (sauf textarea)
              if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") e.preventDefault();
            }}
            className="p-5 space-y-5 text-slate-800 dark:text-slate-100"
          >
            {/* ligne 1 : utilisateur + date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs mb-1 opacity-70">Utilisateur</label>
                <Select
                  className="w-full"
                  classNamePrefix="rs"
                  options={userOptions}
                  value={userOptions.find((u) => u.value === selectedUser) || null}
                  onChange={(sel) => setSelectedUser(sel?.value || "")}
                  placeholder="Sélectionnez un utilisateur…"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    control: (base) => ({
                      ...base,
                      borderRadius: 12,
                      backgroundColor: "transparent",
                    }),
                  }}
                  isDisabled={loading}
                />
              </div>

              <div>
                <label className="block text-xs mb-1 opacity-70">Date d’achat</label>
                <input
                  type="date"
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  value={dateAchat}
                  onChange={(e) => setDateAchat(e.target.value)}
                  required
                />
              </div>
            </div>

            {/* ligne 2 : article */}
            <div>
              <label className="block text-xs mb-1 opacity-70">Article</label>
              <select
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                value={selectedArticle}
                onChange={(e) => setSelectedArticle(e.target.value)}
                required
                disabled={loading}
              >
                <option value="">Sélectionnez un article</option>
                {articles.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.nom}
                  </option>
                ))}
              </select>
              {selectedArticleObj && (
                <p className="mt-1 text-xs opacity-70">
                  Prix unitaire&nbsp;: {Number(selectedArticleObj.prix).toFixed(2)} €
                </p>
              )}
            </div>

            {/* ligne 3 : quantite + statut + type */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs mb-1 opacity-70">Quantité</label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  value={quantite}
                  onChange={(e) => setQuantite(Math.max(1, Number(e.target.value || 1)))}
                  required
                />
              </div>

              <div>
                <label className="block text-xs mb-1 opacity-70">Statut du règlement</label>
                <select
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  value={statutReglement}
                  onChange={(e) => setStatutReglement(e.target.value)}
                  required
                >
                  <option value="">Sélectionnez un statut</option>
                  {STATUS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs mb-1 opacity-70">Type de règlement</label>
                <select
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  value={typeReglement}
                  onChange={(e) => setTypeReglement(e.target.value)}
                  required
                >
                  <option value="">Sélectionnez un type</option>
                  {PAYMENT_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* message */}
            <div>
              <label className="block text-xs mb-1 opacity-70">Message utilisateur</label>
              <textarea
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                rows={4}
                value={messageAchat}
                onChange={(e) => setMessageAchat(e.target.value)}
                placeholder="Précisions, tailles, souhaits…"
              />
            </div>

            {/* résumé / total */}
            <div className="rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 px-4 py-3 text-sm flex items-center justify-between">
              <span className="opacity-80">
                {selectedArticleObj ? selectedArticleObj.nom : "Article non sélectionné"}
                {selectedArticleObj && ` · ${Number(selectedArticleObj.prix).toFixed(2)} € x ${quantite}`}
              </span>
              <span className="font-semibold">
                Total&nbsp;: <span className="tabular-nums">{total} €</span>
              </span>
            </div>

            {/* actions */}
            <div className="flex justify-end gap-2 pt-1">
              <Button
                text="Annuler"
                icon={FaTimes}
                type="button"
                onClick={() => {
                  resetForm();
                  onClose();
                }}
                className="!rounded-xl gap-2"
              />
              <Button
                text="Enregistrer"
                icon={FaSave}
                type="submit"
                className="!rounded-xl gap-2"
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
